import type { VoucherView } from '@voll-intelligence/domain';

import { t } from '@web/i18n';
import { AppIcons } from '@web/ui/core/atoms';
import { StatusBadge, voucherStatusBadge } from '@web/ui/core/molecules';

import { formatAmount } from '../format_date';
import { voucherHeadline, voucherServiceDate, voucherTypeIcon } from '../voucher_presentation';

/**
 * The central primitive of the Viagens / Cancelados tabs: a single voucher as a
 * grid row `[tonal icon | locator + route + meta | status badge | caret]`.
 * Clicking opens the shared detail drawer (`onOpen`) — the one interaction model
 * across both tabs. The bottom hairline is dropped on the list's last row.
 */
export function CustomerVoucherRow({
  voucher,
  onOpen,
}: Readonly<{
  voucher: VoucherView;
  onOpen: (voucher: VoucherView) => void;
}>): React.JSX.Element {
  const badge = voucherStatusBadge(voucher);

  return (
    <li className="border-b border-border-soft last:border-b-0">
      <button
        type="button"
        onClick={() => {
          onOpen(voucher);
        }}
        className="grid w-full grid-cols-voucher-row items-center gap-3 px-5 py-3 text-left transition-colors duration-fast ease-standard hover:bg-muted"
      >
        <span className="flex h-8.5 w-8.5 items-center justify-center rounded-md border border-border-soft bg-muted text-muted-foreground">
          <AppIcons name={voucherTypeIcon(voucher)} size={16} />
        </span>
        <span className="flex min-w-0 flex-col gap-0.75">
          <span className="truncate font-mono text-card-id font-medium text-faint-foreground">
            {voucher.voucherCode ?? '—'}
          </span>
          <span className="truncate text-card-meta font-medium text-foreground">
            {voucherHeadline(voucher)}
          </span>
          <span className="hidden items-center gap-2 truncate font-mono text-card-id text-muted-foreground sm:flex">
            {voucher.modal ? (
              <span className="font-medium uppercase tracking-eyebrow text-faint-foreground">
                {voucher.modal}
              </span>
            ) : null}
            {voucher.modal ? <span className="text-border">·</span> : null}
            <span>{voucherServiceDate(voucher)}</span>
            <span className="text-border">·</span>
            <span>{formatAmount(voucher.amount, voucher.currency)}</span>
          </span>
        </span>
        {badge ? <StatusBadge tone={badge.tone}>{t(badge.labelKey)}</StatusBadge> : <span />}
        <AppIcons name="caret-right" size={15} className="flex-shrink-0 text-faint-foreground" />
      </button>
    </li>
  );
}
